// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { Message } from '../api/models/Message';
import { MessageContent } from '../api/models/MessageContent';
import { Conversation } from '../api/models/Conversation';
import 'katex/dist/katex.min.css';
import Latex from 'react-latex-next';
import ImageModal, { ImgModalState } from './image-modal';
import { ArrowTopRightOnSquareIcon, ArrowDownTrayIcon, BookmarkIcon, ChartBarIcon } from "@heroicons/react/24/solid"; // Importing icons
import { CodeInterpreterResults } from './code-modal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { createArtifact } from '../api/thunks/artifacts';
import { useAppSelector } from '../store/hooks';
import { useDispatch } from 'react-redux';
import { createCheckoutSession } from '../api/thunks/users';
import { parseStructuredMarkdown } from '../utils/structured-markdown';
import { DirectiveRenderer } from './directive-renderer';
import InputWithSuggestions from './input-with-suggestions';
import { Share2Icon, Telescope } from 'lucide-react';
import { setNotification } from '../store/notifications-reducer';
import TypingDots from './typing-dots';
import { WebSearchAnnotation, WebSearchResult } from '../api/models/WebSearchAnnotation';
import { VectorStoreSearchResult } from '../api/models/FileSearchAnnotation';
import { Effort, Model } from '../api/models/CreateMessageInput';
import { FinancialChartModal } from './financial-chart-modal';

// CSV export utility function
const exportTableToCSV = (tableElement: HTMLTableElement, filename: string = 'table-data') => {
    const rows = Array.from(tableElement.querySelectorAll('tr'));
    const csvContent = rows.map(row => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        return cells.map(cell => {
            // Clean the cell content and escape quotes
            const content = cell.textContent?.trim() || '';
            // Escape quotes by doubling them and wrap in quotes if contains comma, quote, or newline
            if (content.includes(',') || content.includes('"') || content.includes('\n')) {
                return `"${content.replace(/"/g, '""')}"`;
            }
            return content;
        }).join(',');
    }).join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

// Chart data extraction utility function
const extractChartDataFromTable = (tableElement: HTMLTableElement) => {
    const rows = Array.from(tableElement.querySelectorAll('tr'));
    if (rows.length < 2) return { data: [], datasets: [] };

    const headerRow = rows[0];
    const headers = Array.from(headerRow.querySelectorAll('th, td')).map(cell => cell.textContent?.trim() || '');

    // Assume first column is period, rest are data columns
    const periodColumn = headers[0];
    const dataColumns = headers.slice(1);

    const data = rows.slice(1).map(row => {
        const cells = Array.from(row.querySelectorAll('th, td'));
        const period = cells[0]?.textContent?.trim() || '';
        const dataPoint: any = { period };

        cells.slice(1).forEach((cell, index) => {
            const columnName = dataColumns[index];
            if (columnName) {
                const cellText = cell.textContent?.trim() || '';
                // Try to parse as number, removing common currency symbols and formatting
                const cleanedText = cellText.replace(/[$,B,M,K,%]/g, '').replace(/[()]/g, '-');
                const numericValue = parseFloat(cleanedText);

                if (!isNaN(numericValue)) {
                    // Handle B, M, K multipliers
                    let multiplier = 1;
                    if (cellText.includes('B')) multiplier = 1000000000;
                    else if (cellText.includes('M')) multiplier = 1000000;
                    else if (cellText.includes('K')) multiplier = 1000;

                    dataPoint[columnName] = numericValue * multiplier;
                } else {
                    // Keep text values as-is instead of converting to 0
                    dataPoint[columnName] = cellText || '';
                }
            }
        });

        return dataPoint;
    });

    // Generate colors for datasets
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16'];
    const datasets = dataColumns.map((column, index) => ({
        label: column,
        units: 'USD', // Default to USD, could be enhanced to detect units
        color: colors[index % colors.length]
    }));

    return { data, datasets };
};

// Simple React component
export const LlmConvoComponent = ({ createShareLink, convo, sendMessage, message, setMessage, getCitationTextDetails, highlighter, setImgModalState, openCodeModal, createArtifact, questionSuggestions, messageInProgress }:
    {
        createShareLink: (conversationId: string) => void;
        convo: Conversation | undefined
        sendMessage: (model: Model, effortLevel?: Effort) => Promise<boolean>;
        message: string;
        setMessage: (message: string) => void;
        getCitationTextDetails: (conversationId: string, messageId: string, fileId: string) => Promise<string>;
        setImgModalState: (state: ImgModalState) => void;
        openCodeModal: (val: string) => void;
        createArtifact: (type: 'code' | 'image', data: string) => Promise<void>;
        questionSuggestions: string[];
        messageInProgress?: boolean;
    }
) => {
    const messageDiv = document.getElementById('message-div');
    const [tooltip, setTooltip] = useState({ visible: false, title: '', position: { x: 0, y: 0 } });
    const [isShowingFullCitation, setIsShowingFullCitation] = useState(false);
    const [messageInFlight, setMessageInFlight] = useState(false);
    const [expandedDetails, setExpandedDetails] = useState<{ [key: string]: boolean }>({});
    const [chartModalOpen, setChartModalOpen] = useState(false);
    const [chartData, setChartData] = useState<{ data: any[], datasets: any[] }>({ data: [], datasets: [] });
    const user = useAppSelector(state => state.user);
    const dispatch = useDispatch();
    const [selectedModel, setSelectedModel] = useState<Model>(() => {
        const query = new URLSearchParams(window.location.search);
        return (query.get('model') as Model) || "o3";
    });
    const [effortLevel, setEffortLevel] = useState<Effort | undefined>("high");

    // Persist model selection in URL
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        if (selectedModel !== query.get('model')) {
            query.set('model', selectedModel);
            window.history.replaceState(null, '', `${window.location.pathname}?${query.toString()}`);
        }
    }, [selectedModel]);

    const memoizedMessageData = useMemo(() => {
        const cache = new Map();
        if (convo && convo.messages) {
            for (const msg of convo.messages) {
                if (msg.id && msg.role !== Message.RoleEnum.User && msg.runDetails) {
                    const fileSearchToolCalls = msg.runDetails.filter(call => call.type === "function" && call.name == "file_search") || [];
                    let vectorStoreSearchResults: VectorStoreSearchResult[] = [];
                    for (const call of fileSearchToolCalls) {
                        try {
                            if (typeof call.output === 'string') {
                                const parsedOutput = JSON.parse(call.output);
                                if (parsedOutput && Array.isArray(parsedOutput.results)) {
                                    vectorStoreSearchResults.push(...parsedOutput.results);
                                }
                            }
                        } catch (e) {
                            // console.log("failed to json parse fileSearchToolCalls output", call, e); // Log includes error object e
                        }
                    }
                    cache.set(msg.id, { fileSearchToolCalls, vectorStoreSearchResults });
                } else if (msg.id) {
                    cache.set(msg.id, { fileSearchToolCalls: [], vectorStoreSearchResults: [] });
                }
            }
        }
        return cache;
    }, [convo?.messages]);


    const showTooltip = (title: string, event: React.MouseEvent) => {
        const tooltipHeight = 100; // Height of the tooltip in px
        const yOffset = 10; // Offset from the clicked citation

        setTooltip({
            visible: true,
            title: title,
            position: { x: event.clientX, y: event.clientY - tooltipHeight - yOffset } // Adjust tooltip position to appear above
        });
    };

    const hideTooltip = () => {
        setTooltip({ visible: false, title: '', position: { x: 0, y: 0 } });
    };


    // useEffect(() => {
    //     if (messageDiv) {
    //         messageDiv.scrollTop = messageDiv.scrollHeight;
    //     }
    // }, [messageDiv, convo]);

    function addCitationLinksToMessage(
        content: MessageContent,
        rootMessage?: Message,
        msgIdx: number
    ) {
        const messageSpecificData = rootMessage?.id ? memoizedMessageData.get(rootMessage.id) : undefined;
        const currentVectorStoreSearchResults = messageSpecificData?.vectorStoreSearchResults || [];

        if (!content?.text) {
            return null;
        }

        // Step 0: Process structured markdown directives first
        const { cleanedText, directives } = parseStructuredMarkdown(content.text);
        
        // Create a new content object with cleaned text
        const processedContent = { ...content, text: cleanedText };

        // Step 1: Extract LaTeX sections first - be more specific to avoid breaking table content
        const latexRegex = /(\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\])/g;
        const latexDelimiters = [
            { left: "$$", right: "$$", display: true },
            { left: "\\[", right: "\\]", display: true },
        ];

        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let partIndex = 0;

        // Process LaTeX sections
        for (const latexMatch of processedContent.text.matchAll(latexRegex)) {
            const startIndex = latexMatch.index!;
            const endIndex = startIndex + latexMatch[0].length;

            // Process text before LaTeX
            if (lastIndex < startIndex) {
                const textSegment = processedContent.text.slice(lastIndex, startIndex);
                parts.push(processTextSegment(textSegment, currentVectorStoreSearchResults, partIndex++, rootMessage, msgIdx));
            }

            // Add LaTeX
            parts.push(
                <div
                    key={`latex-container-${startIndex}-${partIndex++}`}
                    style={{ overflowX: 'scroll' }}
                >
                    <Latex delimiters={latexDelimiters}>
                        {latexMatch[0]}
                    </Latex>
                </div>
            );

            lastIndex = endIndex;
        }

        // Process remaining text after last LaTeX
        if (lastIndex < processedContent.text.length) {
            const textSegment = processedContent.text.slice(lastIndex);
            parts.push(processTextSegment(textSegment, currentVectorStoreSearchResults, partIndex++, rootMessage, msgIdx));
        }

        // If no LaTeX was found, process the entire text
        if (parts.length === 0) {
            parts.push(processTextSegment(processedContent.text, currentVectorStoreSearchResults, 0, rootMessage, msgIdx));
        }

        // Add rendered directives at the end
        if (directives.length > 0) {
            directives.forEach((directive, index) => {
                parts.push(
                    <DirectiveRenderer 
                        key={`directive-${index}`} 
                        directive={directive}
                    />
                );
            });
        }

        return <>{parts}</>;
    }

    function processTextSegment(
        text: string,
        vectorStoreSearchResults: VectorStoreSearchResult[],
        partIndex: number,
        rootMessage?: Message,
        msgIdx: number = 0
    ): React.ReactNode {
        if (!text.trim()) return null;

        // Step 1: Process through ReactMarkdown first
        const citationRegex = /【([^†]+?)†?(.+?)】/g;
        const citationMatches = []; // Array.from(text.matchAll(citationRegex)); // temporarily disable citation formatting because it breaks tables and the transcript lookup isn't even that great right now

        if (citationMatches.length === 0) {
            // No citations, just render as markdown
            return (
                <ReactMarkdown
                    key={`markdown-${partIndex}`}
                    remarkPlugins={[remarkGfm]} components={{
                        p: ({ node, ...props }) => {
                            // Convert bullet point lines to proper list items
                            const content = props.children;
                            if (typeof content === 'string' && (content.includes('•') || content.includes('✓'))) {
                                const lines = content.split('\n').filter(line => line.trim());
                                const hasMultipleBullets = lines.filter(line => line.includes('•') || line.includes('✓')).length > 1;

                                if (hasMultipleBullets) {
                                    return (
                                        <ul className="list-disc ml-5 mb-2">
                                            {lines.map((line, index) => {
                                                if (line.includes('•') || line.includes('✓')) {
                                                    const cleanLine = line.replace(/^[\s]*[•✓][\s]*/, '').trim();
                                                    return <li key={index} className="mb-1">{cleanLine}</li>;
                                                } else if (line.trim()) {
                                                    return <p key={index} className="mb-2">{line}</p>;
                                                }
                                                return null;
                                            })}
                                        </ul>
                                    );
                                }
                            }
                            return <p className="mb-2" {...props} />;
                        },
                        ul: ({ node, ...props }) => (
                            <ul className="list-disc ml-5" {...props} />
                        ),
                        li: ({ node, ...props }) => (
                            <li className="mb-1" {...props} />
                        ),
                        a: ({ node, ...props }) => (
                            <a className="text-blue-500 underline" {...props} />
                        ),
                        table: ({ node, ...props }) => {
                            const tableId = `table-${Math.random().toString(36).substr(2, 9)}`;
                            return (
                                <div className="overflow-x-auto my-4 relative">
                                    <div className="flex justify-end mb-2 gap-2">
                                        <button
                                            onClick={() => {
                                                const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                if (tableElement) {
                                                    const { data, datasets } = extractChartDataFromTable(tableElement);
                                                    setChartData({ data, datasets });
                                                    setChartModalOpen(true);
                                                }
                                            }}
                                            className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                            title="Create Chart"
                                        >
                                            <ChartBarIcon className="w-3 h-3" />
                                            Chart
                                        </button>
                                        <button
                                            onClick={() => {
                                                const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                if (tableElement) {
                                                    exportTableToCSV(tableElement, 'financial-data');
                                                }
                                            }}
                                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                            title="Export to CSV"
                                        >
                                            <ArrowDownTrayIcon className="w-3 h-3" />
                                            CSV
                                        </button>
                                    </div>
                                    <table
                                        className="min-w-full border-collapse border border-gray-300"
                                        data-table-id={tableId}
                                        {...props}
                                    />
                                </div>
                            );
                        },
                        thead: ({ node, ...props }) => (
                            <thead className="bg-gray-50" {...props} />
                        ),
                        tbody: ({ node, ...props }) => (
                            <tbody {...props} />
                        ),
                        tr: ({ node, ...props }) => (
                            <tr className="border-b border-gray-200" {...props} />
                        ),
                        th: ({ node, ...props }) => (
                            <th className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                        ),
                        td: ({ node, ...props }) => (
                            <td className="border border-gray-300 px-4 py-2" {...props} />
                        )
                    }}
                >
                    {convertToMarkdownTable(text)}
                </ReactMarkdown>
            );
        }

        // Step 2: Handle citations by splitting text and wrapping citations
        const parts: React.ReactNode[] = [];
        let lastIndex = 0;
        let citationIndex = 0;

        for (const match of citationMatches) {
            const [citationText, citationTextFileNameOrFileId] = match;
            const startIndex = match.index!;
            const endIndex = startIndex + citationText.length;

            // Add text before citation as markdown
            if (lastIndex < startIndex) {
                const beforeText = text.slice(lastIndex, startIndex);
                if (beforeText.trim()) {
                    parts.push(
                        <ReactMarkdown
                            key={`markdown-before-${partIndex}-${citationIndex}`}
                            remarkPlugins={[remarkGfm]}
                            components={{
                                p: ({ node, ...props }) => {
                                    // Convert bullet point lines to proper list items
                                    const content = props.children;
                                    if (typeof content === 'string' && (content.includes('•') || content.includes('✓'))) {
                                        const lines = content.split('\n').filter(line => line.trim());
                                        const hasMultipleBullets = lines.filter(line => line.includes('•') || line.includes('✓')).length > 1;

                                        if (hasMultipleBullets) {
                                            return (
                                                <ul className="list-disc ml-5 mb-2">
                                                    {lines.map((line, index) => {
                                                        if (line.includes('•') || line.includes('✓')) {
                                                            const cleanLine = line.replace(/^[\s]*[•✓][\s]*/, '').trim();
                                                            return <li key={index} className="mb-1">{cleanLine}</li>;
                                                        } else if (line.trim()) {
                                                            return <p key={index} className="mb-2">{line}</p>;
                                                        }
                                                        return null;
                                                    })}
                                                </ul>
                                            );
                                        }
                                    }
                                    return <p className="mb-2" {...props} />;
                                },
                                ul: ({ node, ...props }) => (
                                    <ul className="list-disc ml-5" {...props} />
                                ),
                                li: ({ node, ...props }) => (
                                    <li className="mb-1" {...props} />
                                ),
                                a: ({ node, ...props }) => (
                                    <a className="text-blue-500 underline" {...props} />
                                ),
                                table: ({ node, ...props }) => {
                                    const tableId = `table-${Math.random().toString(36).substr(2, 9)}`;
                                    return (
                                        <div className="overflow-x-auto my-4 relative">
                                            <div className="flex justify-end mb-2 gap-2">
                                                <button
                                                    onClick={() => {
                                                        const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                        if (tableElement) {
                                                            const { data, datasets } = extractChartDataFromTable(tableElement);
                                                            setChartData({ data, datasets });
                                                            setChartModalOpen(true);
                                                        }
                                                    }}
                                                    className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                    title="Create Chart"
                                                >
                                                    <ChartBarIcon className="w-3 h-3" />
                                                    Chart
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                        if (tableElement) {
                                                            exportTableToCSV(tableElement, 'financial-data');
                                                        }
                                                    }}
                                                    className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                    title="Export to CSV"
                                                >
                                                    <ArrowDownTrayIcon className="w-3 h-3" />
                                                    CSV
                                                </button>
                                            </div>
                                            <table
                                                className="min-w-full border-collapse border border-gray-300"
                                                data-table-id={tableId}
                                                {...props}
                                            />
                                        </div>
                                    );
                                },
                                thead: ({ node, ...props }) => (
                                    <thead className="bg-gray-50" {...props} />
                                ),
                                tbody: ({ node, ...props }) => (
                                    <tbody {...props} />
                                ),
                                tr: ({ node, ...props }) => (
                                    <tr className="border-b border-gray-200" {...props} />
                                ),
                                th: ({ node, ...props }) => (
                                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                                ),
                                td: ({ node, ...props }) => (
                                    <td className="border border-gray-300 px-4 py-2" {...props} />
                                )
                            }}
                        >
                            {convertToMarkdownTable(beforeText)}
                        </ReactMarkdown>
                    );
                }
            }

            // Add citation link
            let fullCitation = '';
            for (const c of vectorStoreSearchResults) {
                if (c.file_id === citationTextFileNameOrFileId || c.filename === citationTextFileNameOrFileId) {
                    fullCitation += c.content.map((contentItem) => contentItem.text).join('\n');
                }
            }

            parts.push(
                <a
                    key={`citation-${partIndex}-${citationIndex}`}
                    className="text-blue-500 hover:underline cursor-pointer break-words"
                    style={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        hyphens: 'auto'
                    }}
                    onClick={async (e) => {
                        setIsShowingFullCitation(true);
                        if (window.location.href.includes('try-it')) {
                            fullCitation = await getCitationTextDetails(convo?.id || '', rootMessage?.id || '', citationTextFileNameOrFileId);
                        }
                        showTooltip(
                            `${citationText.split(".")[0]} ${fullCitation}`,
                            e
                        );
                    }}
                >
                    {citationText}
                </a>
            );

            lastIndex = endIndex;
            citationIndex++;
        }

        // Add remaining text after last citation as markdown
        if (lastIndex < text.length) {
            const afterText = text.slice(lastIndex);
            if (afterText.trim()) {
                parts.push(
                    <ReactMarkdown
                        key={`markdown-after-${partIndex}`}
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ node, ...props }) => {
                                // Convert bullet point lines to proper list items
                                const content = props.children;
                                if (typeof content === 'string' && (content.includes('•') || content.includes('✓'))) {
                                    const lines = content.split('\n').filter(line => line.trim());
                                    const hasMultipleBullets = lines.filter(line => line.includes('•') || line.includes('✓')).length > 1;

                                    if (hasMultipleBullets) {
                                        return (
                                            <ul className="list-disc ml-5 mb-2">
                                                {lines.map((line, index) => {
                                                    if (line.includes('•') || line.includes('✓')) {
                                                        const cleanLine = line.replace(/^[\s]*[•✓][\s]*/, '').trim();
                                                        return <li key={index} className="mb-1">{cleanLine}</li>;
                                                    } else if (line.trim()) {
                                                        return <p key={index} className="mb-2">{line}</p>;
                                                    }
                                                    return null;
                                                })}
                                            </ul>
                                        );
                                    }
                                }
                                return <p className="mb-2" {...props} />;
                            },
                            ul: ({ node, ...props }) => (
                                <ul className="list-disc ml-5" {...props} />
                            ),
                            li: ({ node, ...props }) => (
                                <li className="mb-1" {...props} />
                            ),
                            a: ({ node, ...props }) => (
                                <a className="text-blue-500 underline" {...props} />
                            ),
                            table: ({ node, ...props }) => {
                                const tableId = `table-${Math.random().toString(36).substr(2, 9)}`;
                                return (
                                    <div className="overflow-x-auto my-4 relative">
                                        <div className="flex justify-end mb-2 gap-2">
                                            <button
                                                onClick={() => {
                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                    if (tableElement) {
                                                        const { data, datasets } = extractChartDataFromTable(tableElement);
                                                        setChartData({ data, datasets });
                                                        setChartModalOpen(true);
                                                    }
                                                }}
                                                className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                title="Create Chart"
                                            >
                                                <ChartBarIcon className="w-3 h-3" />
                                                Chart
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                    if (tableElement) {
                                                        exportTableToCSV(tableElement, 'financial-data');
                                                    }
                                                }}
                                                className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                title="Export to CSV"
                                            >
                                                <ArrowDownTrayIcon className="w-3 h-3" />
                                                CSV
                                            </button>
                                        </div>
                                        <table
                                            className="min-w-full border-collapse border border-gray-300"
                                            data-table-id={tableId}
                                            {...props}
                                        />
                                    </div>
                                );
                            },
                            thead: ({ node, ...props }) => (
                                <thead className="bg-gray-50" {...props} />
                            ),
                            tbody: ({ node, ...props }) => (
                                <tbody {...props} />
                            ),
                            tr: ({ node, ...props }) => (
                                <tr className="border-b border-gray-200" {...props} />
                            ),
                            th: ({ node, ...props }) => (
                                <th className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                            ),
                            td: ({ node, ...props }) => (
                                <td className="border border-gray-300 px-4 py-2" {...props} />
                            )
                        }}
                    >
                        {convertToMarkdownTable(afterText)}
                    </ReactMarkdown>
                );
            }
        }

        return <>{parts}</>;
    }

    const sendMessageLocal = async (messageOverride?: string) => {
        setMessageInFlight(true);
        const res = await sendMessage(selectedModel, effortLevel, messageOverride);
        if (res) {
            setMessage('');
        }
        setMessageInFlight(false);
    }

    const handleToggleDetails = (id: string) => {
        setExpandedDetails(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const formatFunctionName = (str) => {
        if (!str) {
            return "";
        }
        // Replace underscores with spaces, then add space before capital letters and convert to lowercase
        return str
            .replace(/_/g, ' ')                    // Replace underscores with spaces
            .replace(/([a-z])([A-Z])/g, '$1 $2')    // Add space before capital letters
            .toLowerCase();                        // Convert the entire string to lowercase
    };

    const formatFuncCallOutput = (call: any) => {
        if (call.name === 'web_search') {
            try {
                const res = JSON.parse(call.output) as WebSearchResult;
                return <ul className='list-disc ml-5'>
                    {res.annotations.map((annotation, idx) => {
                        const annotationId = `web-search-annotation-${call.id}-${idx}`;
                        return (
                            <li key={annotationId} className="mb-2 li-disc">
                                <a
                                    className="tool-title text-blue-400 font-size-10 cursor-pointer"
                                    href={annotation.url}
                                    target="_blank"
                                >
                                    {annotation.title}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            } catch (e) {
                return call.output;
            }
        } else if (call.name === 'file_search') {
            try {
                const toolCallResults = JSON.parse(call.output);
                const fileSearchResults = toolCallResults.results as VectorStoreSearchResult[]; // Ensure type consistency
                return fileSearchResults.map((result, idx) => {
                    const fileId = `file-search-result-${call.id}-${idx}`;
                    return (
                        <div key={fileId} className="mb-2">
                            <a
                                className="tool-title text-blue-400 cursor-pointer"
                                onClick={() => handleToggleDetails(fileId)}
                            >
                                {result.file_name || result.file_id}
                            </a>
                            {expandedDetails[fileId] && (
                                <div className="ml-4 mt-1 p-2 border-l-4 border-blue-300 bg-blue-50">
                                    {result.content.map((contentItem, contentIdx) => (
                                        <p key={contentIdx} style={{ overflowWrap: "break-word" }}>
                                            {contentItem.text}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                });
            } catch (e) {
                console.log('Error parsing file search results:', e);
                return call.output
            }
        }

        return call.output
    }

    const renderRunDetails = (msg: Message) => {

        const allFunctionCalls = msg.runDetails.filter(call => call.type === "function" && call.name !== "file_search") || [];

        const allFileSearchCalls = msg.runDetails.filter(call => call.type === "function" && call.name == "file_search") || [];
        const allCodeInterpreterCalls = msg.runDetails.filter(call => call.type === "code_interpreter") || [];
        const funcId = `function-calls-group-${msg.id}`;
        const fileSearchId = `file-search-group-${msg.id}`;
        const codeInterpreterId = `code-interpreter-group-${msg.id}`;
        if (allFunctionCalls.length === 0 && allFileSearchCalls.length === 0 && allCodeInterpreterCalls.length === 0) {
            return <></>;
        }
        return (
            <div key={funcId} className="mt-4">
                {/* Subtle hint for Function Calls */}
                {allFunctionCalls.length > 0 && (
                    <div className="mb-4 p-2 border border-gray-200 rounded bg-gray-50">
                        <div
                            className="tool-title text-sm text-gray-500 cursor-pointer"
                            onClick={() => handleToggleDetails(funcId)}
                        >
                            {expandedDetails[funcId] ? "▲ Hide Tool Use" : "► Show Tool Use"}
                        </div>
                        {expandedDetails[funcId] && (
                            <div className="ml-4 mt-2 text-gray-700">
                                {allFunctionCalls.map((call, idx) => {
                                    if (!call.name || !call.args || !call) return <></>
                                    const callId = call.id;
                                    return (
                                        <div key={callId} className="mb-2">
                                            <div
                                                className="tool-title text-blue-400 cursor-pointer"
                                                onClick={() => handleToggleDetails(callId)}
                                            >
                                                {formatFunctionName(call.name)}
                                            </div>
                                            {expandedDetails[callId] && (
                                                <div className="ml-4 mt-2 p-2 border-l-4 border-blue-300 bg-blue-50 rounded">
                                                    <p style={{ overflowWrap: "break-word", whiteSpace: "pre-wrap" }}>
                                                        <strong>Arguments:</strong>
                                                        <br />
                                                        <code style={{ fontSize: "12px" }}>
                                                            {JSON.stringify(JSON.parse(call.args), null, 2)}
                                                        </code>
                                                    </p>
                                                    <div style={{ overflowWrap: "break-word", whiteSpace: "pre-wrap" }}>
                                                        <strong>Output:</strong> {formatFuncCallOutput(call)}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Subtle hint for File Search Results */}
                {allFileSearchCalls.length > 0 && (
                    <div className="mb-4 p-2 border border-gray-200 rounded bg-gray-50">
                        <div
                            className="tool-title text-sm text-gray-500 cursor-pointer"
                            onClick={() => handleToggleDetails(fileSearchId)}
                        >
                            {expandedDetails[fileSearchId]
                                ? "▲ Hide File Search Results"
                                : "► Show File Search Results"}
                        </div>
                        {expandedDetails[fileSearchId] && (
                            <div className="ml-4 mt-2 text-gray-700">
                                {allFileSearchCalls.map((call, idx) => {
                                    let results = [];
                                    try {
                                        const js = JSON.parse(call.output);
                                        results = js.results;
                                    } catch (e) {
                                        console.error('error parsing call.output', e)
                                        results = [
                                            {
                                                file_name: "error",
                                                content: [
                                                    {
                                                        text: call.output
                                                    }
                                                ]
                                            }
                                        ]
                                    }
                                    return results.map((file, fileIdx) => {
                                        const fileId = `${fileSearchId}-file-${call.id}-${fileIdx}`;
                                        return (
                                            <div key={fileId}>
                                                <span
                                                    className="text-blue-400 cursor-pointer"
                                                    onClick={() => handleToggleDetails(fileId)}
                                                >
                                                    {file.file_name || file.filename || file.file_id}
                                                </span>
                                                {expandedDetails[fileId] && (
                                                    <div className="ml-4 mt-1 p-2 border-l-4 border-blue-300 bg-blue-50">
                                                        {file.content.map((contentItem, contentIdx) => (
                                                            <p key={contentIdx} style={{ overflowWrap: "break-word" }}>
                                                                {contentItem.text}
                                                            </p>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Subtle hint for Code Interpreter Results */}
                {allCodeInterpreterCalls.length > 0 && (
                    <CodeInterpreterResults
                        expandedDetails={expandedDetails}
                        handleToggleDetails={handleToggleDetails}
                        id={codeInterpreterId}
                        allCodeInterpreterCalls={allCodeInterpreterCalls}
                        highlighter={highlighter}
                        openCodeModal={openCodeModal}
                    />
                )}
            </div>
        );
    };

    // Carousel for images from runDetails
    function RenderImageCarousel({ msg }: { msg: Message }) {
        const images = [];
        if (msg.runDetails) {
            for (const call of msg.runDetails) {
                if (call.images && Array.isArray(call.images)) {
                    for (const img of call.images) {
                        images.push(img);
                    }
                }
            }
        }
        const [carouselIdx, setCarouselIdx] = useState(0);
        if (images.length === 0) return null;
        return (
            <div className="w-full flex flex-col items-center my-4">
                <div className="relative w-full max-w-xs flex flex-col items-center">
                    <img
                        src={images[carouselIdx].data}
                        alt={`Image ${carouselIdx + 1}`}
                        className="mb-2 cursor-pointer object-contain w-full max-h-64 rounded shadow"
                        onClick={() => {
                            setImgModalState({
                                isImageModalOpen: true,
                                modalImageSrc: images[carouselIdx].data,
                            });
                        }}
                    />
                    <div className="absolute bottom-2 right-2 flex space-x-4">
                        <button
                            className="bg-black bg-opacity-50 text-white flex items-center justify-center p-2 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => {
                                setImgModalState({
                                    isImageModalOpen: true,
                                    modalImageSrc: images[carouselIdx].data,
                                });
                            }}
                        >
                            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                        </button>
                        <button
                            className="bg-black bg-opacity-50 text-white flex items-center justify-center p-2 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => {
                                const link = document.createElement("a");
                                link.href = images[carouselIdx].data;
                                link.download = `image-${carouselIdx + 1}.png`;
                                link.click();
                            }}
                        >
                            <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                        <button
                            className="bg-black bg-opacity-50 text-white flex items-center justify-center p-2 rounded-full hover:bg-opacity-75 focus:outline-none focus:ring-2 focus:ring-white"
                            onClick={() => {
                                let hint = undefined;
                                if (msg.content?.length > 0) {
                                    hint = msg.content[0].text
                                }
                                createArtifact("image", images[carouselIdx].data, hint);
                            }}
                        >
                            <BookmarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>
                <div className="flex items-center mt-2 space-x-2">
                    <button
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                        onClick={() => setCarouselIdx((carouselIdx - 1 + images.length) % images.length)}
                        disabled={images.length <= 1}
                    >
                        ◀
                    </button>
                    <span className="text-sm text-gray-600">{carouselIdx + 1} / {images.length}</span>
                    <button
                        className="px-2 py-1 bg-gray-200 rounded disabled:opacity-50"
                        onClick={() => setCarouselIdx((carouselIdx + 1) % images.length)}
                        disabled={images.length <= 1}
                    >
                        ▶
                    </button>
                </div>
            </div>
        );
    };

    // Function to extract the latest title from thinking summary
    function extractLatestTitle(text: string): string {
        if (!text) return "Show Thinking";
        
        // Look for bold text patterns (**text**) which are used as titles in thinking summaries
        const boldTitleRegex = /\*\*([^*]+)\*\*/g;
        const matches = [];
        let match;
        
        while ((match = boldTitleRegex.exec(text)) !== null) {
            matches.push(match[1].trim());
        }
        
        // Return the last found title, or default text if none found
        return matches.length > 0 ? matches[matches.length - 1] : "Show Thinking";
    }

    // Function to convert pipe-separated content to proper markdown tables and handle bullet points
    function convertToMarkdownTable(text: string): string {
        // First, handle bullet points by converting them to proper markdown list format
        const bulletPointRegex = /^[\s]*[•✓][\s]*/gm;
        let processedText = text.replace(bulletPointRegex, '* ');

        // Also handle cases where bullet points appear mid-paragraph
        processedText = processedText.replace(/\n[\s]*[•✓][\s]*/g, '\n* ');

        const lines = processedText.split('\n');
        const result: string[] = [];
        let i = 0;

        // Helper function to format numbers in table cells
        const formatTableCell = (cell: string): string => {
            const trimmed = cell.trim();
            // Check if the cell looks like a number with space separators (e.g., "15 376")
            if (/^\d{1,3}(?:\s\d{3})*$/.test(trimmed)) {
                // Replace spaces with commas for proper number formatting
                return trimmed.replace(/\s/g, ',');
            }
            return trimmed;
        };

        while (i < lines.length) {
            const line = lines[i].trim();

            // Check if this line looks like a table header (contains pipes and isn't empty)
            if (line.includes('|') && line.length > 0) {
                // Look ahead to see if the next few lines also contain pipes
                let tableLines: string[] = [line];
                let j = i + 1;

                // Collect consecutive lines that contain pipes
                while (j < lines.length && lines[j].trim().includes('|') && lines[j].trim().length > 0) {
                    tableLines.push(lines[j].trim());
                    j++;
                }

                // If we found at least 2 lines with pipes, check if it's already a proper markdown table
                if (tableLines.length >= 2) {
                    // Check if the second line is already a separator row (contains only dashes, pipes, and spaces)
                    const secondLine = tableLines[1];
                    const isSeparatorRow = /^[\s\|\-]+$/.test(secondLine);

                    if (isSeparatorRow) {
                        // Already a proper markdown table, just add it as-is but format numbers
                        for (let k = 0; k < tableLines.length; k++) {
                            const tableLine = tableLines[k];
                            if (k === 1) {
                                // Don't format the separator row
                                result.push(tableLine);
                            } else {
                                // Format numbers in data rows
                                const formattedLine = tableLine.replace(/\|([^|]*)\|/g, (match, cell) => {
                                    return `|${formatTableCell(cell)}|`;
                                });
                                result.push(formattedLine);
                            }
                        }
                        result.push(''); // Add empty line after table
                        i = j;
                    } else {
                        // Convert to proper markdown table format
                        const firstLine = tableLines[0];

                        // Check if the first line starts and ends with pipes (already formatted)
                        const isAlreadyFormatted = firstLine.startsWith('|') && firstLine.endsWith('|');

                        if (isAlreadyFormatted) {
                            // Count columns from the first line
                            const columnCount = firstLine.split('|').length - 2; // -2 because of leading and trailing pipes

                            // Add the header row with formatted numbers
                            const formattedHeaderLine = firstLine.replace(/\|([^|]*)\|/g, (match, cell) => {
                                return `|${formatTableCell(cell)}|`;
                            });
                            result.push(formattedHeaderLine);

                            // Add the separator row
                            result.push('|' + ' --- |'.repeat(columnCount));

                            // Add the data rows with formatted numbers
                            for (let k = 1; k < tableLines.length; k++) {
                                const formattedDataLine = tableLines[k].replace(/\|([^|]*)\|/g, (match, cell) => {
                                    return `|${formatTableCell(cell)}|`;
                                });
                                result.push(formattedDataLine);
                            }
                        } else {
                            // Convert pipe-separated format to proper markdown
                            const columnCount = (firstLine.match(/\|/g) || []).length + 1;

                            // Add the header row
                            result.push('| ' + firstLine.split('|').map(cell => formatTableCell(cell)).join(' | ') + ' |');

                            // Add the separator row
                            result.push('|' + ' --- |'.repeat(columnCount));

                            // Add the data rows
                            for (let k = 1; k < tableLines.length; k++) {
                                result.push('| ' + tableLines[k].split('|').map(cell => formatTableCell(cell)).join(' | ') + ' |');
                            }
                        }

                        result.push(''); // Add empty line after table
                        i = j; // Skip the processed lines
                    }
                } else {
                    result.push(line);
                    i++;
                }
            } else {
                result.push(line);
                i++;
            }
        }

        return result.join('\n');
    }

    return (
        <>
            <div className="flex flex-col h-full">
                {/* Sticky Header */}
                <div className="flex items-center sticky top-0 z-10 p-4">
                    <img
                        src={`/company_icons/${convo?.ticker}.png`}
                        alt={`${convo?.ticker}-logo`}
                        className="w-8 h-8"
                        onError={(e) => {
                            console.log('img err', e)
                            e.currentTarget.style.display = 'none'
                        }}
                    />
                    <h1 className="font-bold ml-4 flex items-center">
                        <span className={user?.user?.plan === 'free' ? "max-w-[25ch] truncate" : 'max-w-[30ch] truncate'}>{convo?.name}</span>
                    </h1>

                    {user?.user?.plan === "free" && (
                        <a
                            href="#"
                            onClick={async () => {
                                if (process.env.REACT_APP_PQ_PREMIUM_PRICE_ID === undefined) {
                                    alert("Failed to create checkout session, price id not defined");
                                    return;
                                }
                                const res = await dispatch(
                                    createCheckoutSession({
                                        productCode: process.env.REACT_APP_PQ_PRO_PRICE_ID,
                                    })
                                );
                                if (res.meta.requestStatus === "fulfilled") {
                                    window.location.href = (res.payload as any).redirectUrl;
                                } else {
                                    alert("Failed to create checkout session 4");
                                }
                            }}
                            className="ml-2 flex items-center gap-2 px-3 py-1 text-orange-600 font-semibold border-2 border-orange-600 rounded-lg hover:bg-orange-50 transition"
                        >
                            <span className="text-purple-600 text-sm">💎</span>
                            Upgrade
                        </a>
                    )}

                    {/* <select
                        className='w-1/4 ml-auto bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2.5 appearance-none'
                        value={selectedModel} 
                        onChange={(e)=>{
                            setSelectedModel(e.target.value)
                        }}
                     
                    >
                        <option value="GPT-5">GPT 5</option>
                        <option value="o3-pro">o3 Pro</option>
                        <option value="o3">o3</option>
                        <option value="o3-mini">o3-mini</option>
                        <option value="o4-mini">o4-mini</option>
                        <option value="gpt-4o">GPT 4o</option>
                        <option value="gpt-4o-mini">GPT 4o-mini</option>
                        <option value="gpt-4.1">GPT 4.1</option>
                        <option value="gpt-4.1-mini">GPT 4.1-mini</option>
                        <option value="gpt-4.1-nano">GPT 4.1-nano</option>
                    </select> */}
                </div>


                {/* Scrollable Messages Section */}
                <div
                    id="message-div"
                    className="flex-1 flex flex-col space-y-6 mt-3 scroll-smooth overflow-auto px-4 py-6"
                    style={{ maxWidth: '100%', minWidth: 0 }}
                >
                    {/* Empty conversation - show suggested topics */}
                    {(!convo?.messages || convo.messages.length === 0) && questionSuggestions.length > 0 && (
                        <div className="flex-1 flex flex-col items-center justify-center w-full">
                            <h2 className="text-xl font-semibold text-gray-700 mb-6">Start your research</h2>
                            <div className="grid grid-cols-1 gap-4 max-w-2xl w-full">
                                {questionSuggestions.slice(0, 3).map((suggestion, idx) => (
                                    <button
                                        key={`suggestion-${idx}`}
                                        onClick={() => {
                                            setMessage(suggestion);
                                            sendMessageLocal(suggestion);
                                        }}
                                        className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-blue-300 hover:shadow-md transition-all duration-200 group"
                                    >
                                        <div className="flex items-center justify-between">
                                            <p className="text-gray-700 group-hover:text-gray-900 font-medium">
                                                {suggestion}
                                            </p>
                                            <svg
                                                className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {convo &&
                        convo.messages &&
                        convo.messages.map((msg: any, msgIdx: number) => {
                            if (msg.role === Message.RoleEnum.User) {
                                return (
                                    <div key={`msg-${msg.id}`} className="flex justify-end">
                                        <div className="bg-blue-500 rounded-2xl rounded-br-md py-3 px-4 max-w-sm text-white shadow-sm">
                                            {msg.content.map((c: any, i: any) => (
                                                <p key={`content-${msg.id}-${i}`} className="text-sm leading-relaxed">{c.text}</p>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div key={`msg-${msg.id}`} className="flex justify-center px-4">
                                    <div
                                        className="py-4 px-5 max-w-5xl w-full"
                                        style={{
                                            overflowWrap: 'break-word',
                                            wordBreak: 'break-word',
                                            hyphens: 'auto',
                                            minWidth: 0, // Allows flex items to shrink below content size
                                        }}
                                    >
                                    <RenderImageCarousel msg={msg} />
                                    {msg?.content?.map((c: any, i: any) =>
                                        addCitationLinksToMessage(c, msg, i)
                                    )}

                                    {/* Reasoning Summary Toggle Section */}
                                    {msg.thinkingSummary && (
                                        <div className="mt-2">
                                            <button
                                                className="flex items-center text-gray-600 hover:text-gray-900 focus:outline-none"
                                                onClick={() => handleToggleDetails(`reasoning-summary-${msg.id}`)}
                                            >
                                                <span className="mr-1">
                                                    {expandedDetails[`reasoning-summary-${msg.id}`] ? '▼' : '►'}
                                                </span>
                                                <span className={`font-medium ${messageInProgress && msgIdx === convo.messages.length - 1 ? 'animate-pulse' : ''}`}>{extractLatestTitle(msg.thinkingSummary)}</span>
                                            </button>
                                            {expandedDetails[`reasoning-summary-${msg.id}`] && (
                                                <div className="ml-5 mt-1 p-2 border-l-4 border-gray-300 bg-gray-50 rounded text-sm text-gray-800 whitespace-pre-line">
                                                    <ReactMarkdown
                                                        key={`thinking-summary-markdown-${msg.id}`}
                                                        remarkPlugins={[remarkGfm]}
                                                        components={{
                                                            p: ({ node, ...props }) => (
                                                                <p key={`p-${msg.id}`} className="mb-2" {...props} />
                                                            ),
                                                            ul: ({ node, ...props }) => (
                                                                <ul key={`ul-${msg.id}`} className="list-disc ml-5" {...props} />
                                                            ),
                                                            li: ({ node, ...props }) => (
                                                                <li key={`li-${msg.id}`} className="mb-1" {...props} />
                                                            ),
                                                            a: ({ node, ...props }) => (
                                                                <a key={`a-${msg.id}`} className="text-blue-500 underline" {...props} />
                                                            ),
                                                            strong: ({ node, ...props }) => (
                                                                <>
                                                                    <br />
                                                                    <strong {...props} />
                                                                </>
                                                            ), table: ({ node, ...props }) => {
                                                                const tableId = `table-thinking-${msg.id}-${Math.random().toString(36).substr(2, 9)}`;
                                                                return (
                                                                    <div className="overflow-x-auto my-4 relative">
                                                                        <div className="flex justify-end mb-2 gap-2">
                                                                            <button
                                                                                onClick={() => {
                                                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                                                    if (tableElement) {
                                                                                        const { data, datasets } = extractChartDataFromTable(tableElement);
                                                                                        setChartData({ data, datasets });
                                                                                        setChartModalOpen(true);
                                                                                    }
                                                                                }}
                                                                                className="bg-green-500 hover:bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                                                title="Create Chart"
                                                                            >
                                                                                <ChartBarIcon className="w-3 h-3" />
                                                                                Chart
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    const tableElement = document.querySelector(`table[data-table-id="${tableId}"]`) as HTMLTableElement;
                                                                                    if (tableElement) {
                                                                                        exportTableToCSV(tableElement, 'financial-data');
                                                                                    }
                                                                                }}
                                                                                className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1 transition-colors"
                                                                                title="Export to CSV"
                                                                            >
                                                                                <ArrowDownTrayIcon className="w-3 h-3" />
                                                                                CSV
                                                                            </button>
                                                                        </div>
                                                                        <table
                                                                            key={`table-${msg.id}`}
                                                                            className="min-w-full border-collapse border border-gray-300"
                                                                            data-table-id={tableId}
                                                                            {...props}
                                                                        />
                                                                    </div>
                                                                );
                                                            },
                                                            thead: ({ node, ...props }) => (
                                                                <thead key={`thead-${msg.id}`} className="bg-gray-50" {...props} />
                                                            ),
                                                            tbody: ({ node, ...props }) => (
                                                                <tbody key={`tbody-${msg.id}`} {...props} />
                                                            ),
                                                            tr: ({ node, ...props }) => (
                                                                <tr key={`tr-${msg.id}`} className="border-b border-gray-200" {...props} />
                                                            ),
                                                            th: ({ node, ...props }) => (
                                                                <th key={`th-${msg.id}`} className="border border-gray-300 px-4 py-2 text-left font-semibold bg-gray-100 whitespace-nowrap" {...props} />
                                                            ),
                                                            td: ({ node, ...props }) => (
                                                                <td key={`td-${msg.id}`} className="border border-gray-300 px-4 py-2" {...props} />
                                                            )
                                                        }}
                                                    >
                                                        {convertToMarkdownTable(msg.thinkingSummary)}
                                                    </ReactMarkdown>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {msg.runDetails && renderRunDetails(msg)}
                                    </div>
                                </div>
                            );
                        })}

                    {convo && convo?.messages[convo.messages?.length - 1]?.role === 'user' && (
                        <div className="flex justify-center px-4">
                            <div className="py-4 px-5 max-w-5xl w-full">
                                <TypingDots />
                            </div>
                        </div>
                    )}
                </div>

                {/* Sticky Footer */}
                <div className="sticky bottom-0 z-10 pl-4 pr-4 pt-4">
                    <div className="relative">
                        <InputWithSuggestions suggestions={questionSuggestions} value={message} sendMessage={sendMessageLocal} setMessage={setMessage} />
                    </div>

                    {/* <div
                        className={
                            `mr-auto mt-2 flex items-center p-2 bg-gray-200 w-fit rounded-lg gap-1 ` +
                            (selectedModel === 'o3-pro' ? ' text-blue-800' : ' text-gray-700')
                        }
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            if (selectedModel !== 'o3-pro') {
                                setSelectedModel('o3-pro');
                                setEffortLevel('high');
                            } else {
                                setSelectedModel('o3');
                                setEffortLevel('low');
                            }
                        }}
                    >
                        Deep Research
                        <Telescope className="h-5 w-5 ml-1" />
                    </div> */}
                    {/* Warning Message */}
                    <div className="flex items-center mt-2">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="orange" />
                        </svg>
                        <p
                            className="text-sm text-gray-500 flex items-center"
                            style={{
                                maxHeight: '4vh',
                                overflow: 'auto',
                            }}
                        >
                            AI makes mistakes. Check the facts.
                        </p>
                    </div>
                </div>
            </div >
            {
                tooltip.visible && (
                    <div
                        className="absolute bg-white border border-gray-300 rounded-lg p-2 shadow-lg overflow-y-auto"
                        style={{
                            top: tooltip.position.y,
                            left: tooltip.position.x,
                            maxHeight: '200px', // Adjust the height as needed
                            width: '200px', // Adjust the width as needed
                            zIndex: 1000
                        }}
                        onMouseLeave={hideTooltip}
                    >
                        <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: tooltip.title }}></div>
                    </div>
                )
            }
            <FinancialChartModal
                isOpen={chartModalOpen}
                onClose={() => setChartModalOpen(false)}
                data={chartData.data}
                datasets={chartData.datasets}
            />
        </>
    )
};
